import axios from 'axios';

const APPLICATION_SERVICE_BASE_URL = import.meta.env.VITE_APPLICATION_SERVICE_URL || 'http://localhost:8003';

/**
 * Application data interface
 * Note: candidateId is automatically extracted from JWT token, not sent in request
 */
export interface CreateApplicationRequest {
    jobId: string;
    recruiterId?: string;
    resumeUrl?: string;
    coverLetter?: string;
    profileShareConsent?: boolean;
}

export interface Application {
    id: string;
    jobId: string;
    candidateId: string;
    recruiterId?: string;
    currentStage: 'applied' | 'interview' | 'offered' | 'rejected';
    resumeUrl?: string;
    coverLetter?: string;
    appliedAt: string;
    updatedAt: string;
    candidateName?: string;
    candidateEmail?: string;
    jobTitle?: string;
    companyName?: string;
    status?: string;
    profileShareConsent?: boolean;
}

export interface ApplicationsResponse {
    data: Application[];
    page: number;
    limit: number;
    total: number;
    payload?: {
        data: Application[];
        pagination: {
            totalPages: number;
            currentPage: number;
            totalItems: number;
        };
    };
}

export interface GetApplicationsParams {
    page?: number;
    limit?: number;
    stage?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

/**
 * Application API Service
 * Handles all interactions with the application-service-go backend
 */
class ApplicationApiService {
    /**
     * Get auth token from localStorage
     */
    private getAuthToken(): string | null {
        return localStorage.getItem('authToken');
    }

    /**
     * Get auth headers with Bearer token
     */
    private getAuthHeaders(): Record<string, string> {
        const token = this.getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
    }

    /**
     * Submit a job application
     * @param application Application data
     * @returns Promise<Application>
     */
    async submitApplication(application: CreateApplicationRequest): Promise<Application> {
        try {
            const response = await axios.post<{ status: string; data: Application }>(
                `${APPLICATION_SERVICE_BASE_URL}/applications/v1/apply`,
                application,
                {
                    headers: this.getAuthHeaders(),
                }
            );

            return response.data.data;
        } catch (error: any) {
            console.error('Error submitting application:', error);
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            throw new Error('Failed to submit application');
        }
    }

    /**
     * Get all applications for the current candidate
     * @param params Optional filter and pagination parameters
     * @returns Promise<ApplicationsResponse>
     */
    async getMyApplications(params?: GetApplicationsParams): Promise<ApplicationsResponse> {
        try {
            const queryParams = new URLSearchParams();

            if (params?.page) queryParams.append('page', params.page.toString());
            if (params?.limit) queryParams.append('size', params.limit.toString()); // Backend expects 'size'
            if (params?.stage) queryParams.append('stage', params.stage);
            if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
            if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

            const url = `${APPLICATION_SERVICE_BASE_URL}/applications/v1/my-applications${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

            const response = await axios.get<ApplicationsResponse>(
                url,
                {
                    headers: this.getAuthHeaders(),
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error fetching applications:', error);
            throw error;
        }
    }

    /**
     * Get a specific application by ID
     * @param applicationId Application ID
     * @returns Promise<Application>
     */
    async getApplication(applicationId: string): Promise<Application> {
        try {
            const response = await axios.get<{
                payload: Application;
                status: { msg: string; error: boolean };
            }>(
                `${APPLICATION_SERVICE_BASE_URL}/applications/v1/${applicationId}`,
                {
                    headers: this.getAuthHeaders(),
                }
            );

            // Handle Go service response format
            return response.data.payload;
        } catch (error) {
            console.error('Error fetching application:', error);
            throw error;
        }
    }

    /**
     * Withdraw an application
     * @param applicationId Application ID
     * @returns Promise<void>
     */
    async withdrawApplication(applicationId: string): Promise<void> {
        try {
            await axios.delete(
                `${APPLICATION_SERVICE_BASE_URL}/applications/v1/${applicationId}`,
                {
                    headers: this.getAuthHeaders(),
                }
            );
        } catch (error) {
            console.error('Error withdrawing application:', error);
            throw error;
        }
    }

    /**
     * Check if user has already applied to a job
     * @param jobId Job ID
     * @returns Promise<boolean>
     */
    async hasApplied(jobId: string): Promise<boolean> {
        try {
            const response = await this.getMyApplications();
            return response.data.some(app => app.jobId === jobId);
        } catch (error) {
            console.error('Error checking application status:', error);
            return false;
        }
    }

    /**
     * Alias for getApplication - Get application by ID
     * @param applicationId Application ID
     * @returns Promise<Application>
     */
    async getApplicationById(applicationId: string): Promise<Application> {
        return this.getApplication(applicationId);
    }

    /**
     * Get application statistics for the candidate
     * @returns Promise with statistics data
     */
    async getStatistics(): Promise<any> {
        try {
            const response = await axios.get(
                `${APPLICATION_SERVICE_BASE_URL}/applications/v1/statistics`,
                {
                    headers: this.getAuthHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching statistics:', error);
            throw error;
        }
    }

    /**
     * Get saved jobs for the candidate
     * @returns Promise with saved jobs data
     */
    async getSavedJobs(): Promise<any> {
        try {
            const response = await axios.get(
                `${APPLICATION_SERVICE_BASE_URL}/applications/v1/saved-jobs`,
                {
                    headers: this.getAuthHeaders(),
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching saved jobs:', error);
            throw error;
        }
    }
}

export const applicationApiService = new ApplicationApiService();
